/**
 * Interactive artefact pane — Preview (sandboxed iframe) | Source | Notes.
 * ADR-0097: transpile in parent; iframe sandbox="allow-scripts" only.
 */

import { useState } from "react";

import {
  ARTIFACT_IFRAME_SANDBOX,
  buildArtifactSrcdoc,
  compileArtifactJsx,
} from "../../lib/artifact-srcdoc.js";

type ArtifactPaneProps = {
  title: string;
  bodyMd: string;
  bodyJsx: string;
  /** Vendored React IIFE text (fetched once by the parent page). */
  runtimeJs: string | null;
  onClose: () => void;
  onCopyJsx?: () => void;
  onCopyNotes?: () => void;
};

type Tab = "preview" | "source" | "notes";

function buildPreviewState(
  bodyJsx: string,
  runtimeJs: string | null,
): { srcdoc: string; error: string | null } {
  if (runtimeJs == null) {
    return { srcdoc: "", error: "Loading artifact runtime…" };
  }
  if (!runtimeJs.trim()) {
    return { srcdoc: "", error: "Artifact runtime failed to load." };
  }
  const compiled = compileArtifactJsx(bodyJsx);
  if (!compiled.ok) {
    return { srcdoc: "", error: compiled.error };
  }
  return {
    srcdoc: buildArtifactSrcdoc({
      compiledJs: compiled.compiledJs,
      runtimeJs,
    }),
    error: null,
  };
}

export function ArtifactPane({
  title,
  bodyMd,
  bodyJsx,
  runtimeJs,
  onClose,
  onCopyJsx,
  onCopyNotes,
}: ArtifactPaneProps) {
  const [tab, setTab] = useState<Tab>("preview");
  const [refreshKey, setRefreshKey] = useState(0);
  const preview = buildPreviewState(bodyJsx, runtimeJs);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Interactive artefact: ${title}`}
    >
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-md border border-border bg-surface sm:rounded-md">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
          <h2 className="truncate text-sm font-medium text-fg">{title}</h2>
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              className="rounded px-2 py-1 text-xs text-fg hover:bg-muted"
              onClick={() => setRefreshKey((k) => k + 1)}
            >
              Refresh
            </button>
            {onCopyJsx ? (
              <button
                type="button"
                className="rounded px-2 py-1 text-xs text-fg hover:bg-muted"
                onClick={onCopyJsx}
              >
                Copy JSX
              </button>
            ) : null}
            {onCopyNotes ? (
              <button
                type="button"
                className="rounded px-2 py-1 text-xs text-fg hover:bg-muted"
                onClick={onCopyNotes}
              >
                Copy notes
              </button>
            ) : null}
            <button
              type="button"
              className="rounded px-2 py-1 text-xs text-fg hover:bg-muted"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>

        <div className="flex gap-1 border-b border-border px-3 py-1" role="tablist">
          {(
            [
              ["preview", "Preview"],
              ["source", "Source"],
              ["notes", "Notes"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={
                tab === id
                  ? "rounded bg-muted px-2 py-1 text-xs font-medium text-fg"
                  : "rounded px-2 py-1 text-xs text-muted-fg hover:bg-muted"
              }
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="min-h-[50vh] flex-1 overflow-auto p-3">
          {tab === "preview" && (
            <div className="space-y-2">
              {preview.error ? (
                <pre className="whitespace-pre-wrap rounded border border-border bg-surface-muted p-3 text-sm text-fg">
                  {preview.error}
                </pre>
              ) : null}
              {preview.srcdoc ? (
                <iframe
                  key={refreshKey}
                  title={title}
                  sandbox={ARTIFACT_IFRAME_SANDBOX}
                  srcDoc={preview.srcdoc}
                  className="h-[55vh] w-full rounded border border-border bg-surface"
                />
              ) : null}
            </div>
          )}
          {tab === "source" && (
            <pre className="whitespace-pre-wrap break-words rounded border border-border bg-surface-muted p-3 text-xs text-fg">
              {bodyJsx}
            </pre>
          )}
          {tab === "notes" && (
            <pre className="whitespace-pre-wrap break-words rounded border border-border bg-surface-muted p-3 text-sm text-fg">
              {bodyMd}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
